import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Columns3, Image, Loader2, PanelLeft, PanelRight } from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// "L'affiche": the module of the vitrine layout where one photograph holds
// still while the stories scroll past it. Stored as
// websites.theme_config.showcase = { slug?, side, count, enabled }.
//
// The subject follows the pinned article by default (the "À la une" card above),
// so most of the time there is nothing to set here: pin a story and its
// photograph is the one that stays. `slug` overrides the subject WITHOUT moving
// the front page lead, which is the one thing the pin cannot do.
//
// Saving stamps render_requested_at, so the render worker rebuilds the site on
// its next tour.

type Showcase = { slug?: string | null; side?: string; count?: number; enabled?: boolean };
type Fold = "split" | "three";
type Article = { id: string; title: string; slug: string; published_at: string | null };

const db = supabase as any;
const FOLLOW_PIN = "__pinned__";

export default function ShowcaseCard({ siteId }: { siteId: string }) {
  const queryClient = useQueryClient();
  const [slug, setSlug] = useState(FOLLOW_PIN);
  const [side, setSide] = useState("left");
  const [count, setCount] = useState("6");
  const [enabled, setEnabled] = useState(true);
  const [fold, setFold] = useState<Fold>("split");

  const { data: site, isLoading } = useQuery({
    queryKey: ["website-showcase", siteId],
    queryFn: async () => {
      const { data, error } = await db
        .from("websites")
        .select("id, domain, theme_config")
        .eq("id", siteId)
        .single();
      if (error) throw error;
      return data as { id: string; domain: string | null; theme_config: Record<string, any> | null };
    },
    enabled: !!siteId,
  });

  const { data: articles = [] } = useQuery({
    queryKey: ["website-pin-articles", siteId],
    queryFn: async () => {
      const { data, error } = await db
        .from("articles")
        .select("id, title, slug, published_at")
        .eq("website_id", siteId)
        .eq("status", "published")
        .order("published_at", { ascending: false })
        .limit(300);
      if (error) throw error;
      return data as Article[];
    },
    enabled: !!siteId,
  });

  const themeConfig = site?.theme_config || {};
  const isNative = themeConfig.render_mode === "native";
  const isVitrine = themeConfig.template === "vitrine";
  const current: Showcase = themeConfig.showcase || {};
  const pinnedSlug: string | undefined = themeConfig.pinned?.slug;
  const pinnedTitle = useMemo(
    () => articles.find((a) => a.slug === pinnedSlug)?.title,
    [articles, pinnedSlug],
  );

  // Prefill from what is stored the first time the site loads.
  useEffect(() => {
    if (!site) return;
    setSlug(current.slug || FOLLOW_PIN);
    setSide(current.side === "right" ? "right" : "left");
    setCount(String(current.count ?? 6));
    setEnabled(current.enabled !== false);
    setFold(themeConfig.fold === "three" ? "three" : "split");
  }, [site]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: async () => {
      const next = { ...themeConfig };
      const showcase: Showcase = { side, count: Number(count) || 6 };
      if (slug !== FOLLOW_PIN) showcase.slug = slug;
      if (!enabled) showcase.enabled = false;
      next.showcase = showcase;
      next.fold = fold;
      const updates: Record<string, unknown> = { theme_config: next };
      if (isNative) updates.render_requested_at = new Date().toISOString();
      const { error } = await db.from("websites").update(updates).eq("id", siteId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["website-showcase", siteId] });
      queryClient.invalidateQueries({ queryKey: ["website-detail", siteId] });
      queryClient.invalidateQueries({ queryKey: ["websites"] });
      toast.success("La une est enregistrée", {
        description: isNative ? "Le site sera reconstruit dans les 30 minutes." : undefined,
      });
    },
    onError: (err: any) => toast.error(err.message || "Enregistrement impossible"),
  });

  if (!isLoading && !isVitrine) return null;

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Image className="h-4 w-4" /> La une et l'affiche
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Sur la maquette Vitrine, la une porte l'article principal et les articles qui
          l'entourent, puis une photographie reste fixe pendant que les articles défilent
          à côté d'elle. Par défaut c'est la photo de l'article épinglé ci-dessus.
        </p>

        <div className="space-y-1.5">
          <Label className="text-xs flex items-center gap-1.5">
            <Columns3 className="h-3.5 w-3.5" /> Mise en page de la une
          </Label>
          <Select value={fold} onValueChange={(v) => setFold(v as Fold)}>
            <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="split" className="text-sm">
                Deux colonnes : la une, puis les articles à droite
              </SelectItem>
              <SelectItem value="three" className="text-sm">
                Trois colonnes : le fil à gauche, la une, les articles à droite
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-between rounded-md border border-border p-3">
          <div className="min-w-0">
            <p className="text-sm font-medium">Afficher le module</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {enabled ? "Visible sur la page d'accueil" : "Masqué sur la page d'accueil"}
            </p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} />
        </div>

        <div className="grid gap-3 md:grid-cols-[1fr_150px_120px] md:items-end">
          <div className="space-y-1.5 min-w-0">
            <Label className="text-xs">Sujet</Label>
            <Select value={slug} onValueChange={setSlug}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value={FOLLOW_PIN} className="text-sm">
                  <span className="truncate">
                    Suivre l'article épinglé{pinnedTitle ? ` (${pinnedTitle.slice(0, 40)}…)` : ""}
                  </span>
                </SelectItem>
                {articles.map((a) => (
                  <SelectItem key={a.id} value={a.slug} className="text-sm">
                    <span className="truncate">{a.title}</span>
                    {a.published_at && (
                      <span className="ml-2 text-xs text-muted-foreground">
                        {format(new Date(a.published_at), "d MMM", { locale: fr })}
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Côté de la photo</Label>
            <Select value={side} onValueChange={setSide}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="left" className="text-sm">
                  <PanelLeft className="mr-1.5 inline h-3.5 w-3.5" />Gauche
                </SelectItem>
                <SelectItem value="right" className="text-sm">
                  <PanelRight className="mr-1.5 inline h-3.5 w-3.5" />Droite
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Articles</Label>
            <Select value={count} onValueChange={setCount}>
              <SelectTrigger className="h-9 text-sm"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                  <SelectItem key={n} value={String(n)} className="text-sm">{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex justify-end">
          <Button size="sm" className="h-9" onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            <span className={save.isPending ? "ml-1.5" : ""}>Enregistrer</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
